<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    import moment from 'moment';
    import { sqlQuery } from '@/shared/helpers/api';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow.js';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';

    type ContractRow = {
        ContractName: string;
        Description: string;
        Project: string;
        CompanyName: string;
        ContractEndDate: string | null;
    };

    function formatDate(date: string | null): string {
        return date ? moment(date).format('YYYY-MM-DD') : '';
    }

    const props = defineProps<{
        codeId: string;
    }>();

    const emit = defineEmits<{
        'go-to-contract': [];
    }>();

    const { showError } = useShow();

    const referringCodes = ref<ContractRow[] | null>(null);
    const isSyncing = ref(true);
    const errorMessage = ref('');

    async function fetchData(): Promise<ContractRow[]> {
        try {
            // A contract is related to the project master either through the direct
            // CodeRef attribute Contract.ProjectMaster, or through the Contract_ProjectMaster
            // relation library, or both. Company is left joined so that contracts without a
            // contractor are still listed.
            const query
                = `SELECT DISTINCT
                    [Contract].Name as ContractName,
                    [Contract].Description,
                    [Contract].ProjectMaster as Project,
                    [Company].Name as CompanyName,
                    [Contract].ContractEndDate
                FROM [Contract]
                    LEFT JOIN [Company] ON [Contract].Contractor_ID = [Company].Id
                    LEFT JOIN [Contract_ProjectMaster]
                        ON [Contract_ProjectMaster].Contract_ID = [Contract].Id
                        AND [Contract_ProjectMaster].[IsValid] = 1
                        AND [Contract_ProjectMaster].ProjectMaster = @projectIdentity
                WHERE [Contract].IsValid = 1
                  AND ([Contract].ProjectMaster = @projectIdentity
                       OR [Contract_ProjectMaster].Contract_ID IS NOT NULL)`;
            return (await sqlQuery(http, { query, arguments: { projectIdentity: props.codeId } })).data as ContractRow[];
        } catch {
            const msg = 'Failed to fetch contracts';
            showError(msg);
            errorMessage.value = msg;
            return [];
        }
    }

    async function refresh() {
        referringCodes.value = await fetchData();
    }

    defineExpose({ refresh });

    onMounted(async () => {
        referringCodes.value = await fetchData();
        isSyncing.value = false;
    });
</script>

<template>
    <nav
        style="position: relative"
        class="panel is-success">
        <p class="panel-heading">
            Contracts
            <a
                v-require-can-edit-code="{ libraryName: 'Contract' }"
                class="button is-pulled-right is-small"
                @click="emit('go-to-contract')">
                <b-icon
                    icon="pencil"
                    size="is-small" />
            </a>
        </p>
        <div>
            <b-table
                v-if="referringCodes"
                :data="referringCodes"
                default-sort="name"
                :striped="false"
                :narrowed="true"
                :hoverable="false">
                <b-table-column
                    v-slot="col"
                    field="name"
                    label="Name">
                    {{ col.row.ContractName }}
                </b-table-column>
                <b-table-column
                    v-slot="col"
                    label="Company">
                    {{ col.row.CompanyName }}
                </b-table-column>
                <b-table-column
                    v-slot="col"
                    field="description"
                    label="Description">
                    {{ col.row.Description }}
                </b-table-column>
                <b-table-column
                    v-slot="col"
                    field="contractEndDate"
                    label="Contract End Date">
                    {{ formatDate(col.row.ContractEndDate) }}
                </b-table-column>
                <template #empty>
                    <div class="has-text-centered">
                        {{ errorMessage || 'No contracts found' }}
                    </div>
                </template>
            </b-table>
            <b-loading
                v-model="isSyncing"
                :is-full-page="false"
                :can-cancel="false" />
        </div>
    </nav>
</template>
